import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { DropdownOption } from 'src/app/core/interfaces/ui/dropdown.interface';
import { DownloadReportRequest } from 'src/app/core/interfaces/api/report.interface';
import { DEFAULT_NGX_DATATABLE_PAGINATION } from 'src/app/core/helpers/ui/ngx-datatable.constant';
import { EXPORT_OPTIONS } from 'src/app/core/helpers/global/reports.constant';
import { ReportDownloadService } from 'src/app/core/services/ui/report-download.service';

@Component({
  selector: 'shared-dropdown-report-export',
  templateUrl: './dropdown-report-export.component.html',
})
export class DropdownReportExportComponent implements OnInit {
  @Input() module: string = '';
  @Input() currentFilters: any = {};
  @Input() currentLimit: number = DEFAULT_NGX_DATATABLE_PAGINATION.LIMIT;
  @Input() currentPage: number = DEFAULT_NGX_DATATABLE_PAGINATION.PAGE;

  @Input() buttonIcon: string = 'fa fa-download';
  @Input() buttonText: string = 'words.exportData';
  @Input() buttonClass: string = 'btn btn-outline-secondary p-2';
  @Input() disabled: boolean = false;
  @Input() size: 'sm' | 'lg' | '' = '';
  @Input() tooltipText: string = 'words.exportDataTooltip';
  @Input() dropdownId: string = 'reportExportDropdown';

  @Output() exportRequested = new EventEmitter<DownloadReportRequest>();

  public exportOptions: DropdownOption[] = [];

  constructor(public reportDownloadService: ReportDownloadService) {}

  ngOnInit(): void {
    this.setupExportOptions();
  }

  private setupExportOptions(): void {
    this.exportOptions = [...EXPORT_OPTIONS];
  }

  public onExportSelected(optionValue: string): void {
    const exportRequest = this.buildExportRequest(optionValue);
    if (exportRequest) {
      this.exportRequested.emit(exportRequest);
    }
  }

  private buildExportRequest(
    optionValue: string
  ): DownloadReportRequest | null {
    const [dataType, reportType] = optionValue.split('-');

    if (!dataType || !reportType) {
      return null;
    }

    if (
      !this.isValidDataType(dataType) ||
      !this.isValidReportType(reportType)
    ) {
      return null;
    }

    const baseRequest: DownloadReportRequest = {
      dataType: dataType as 'excel' | 'csv',
      reportType:
        reportType === 'current' ? 'limit' : (reportType as 'all' | 'range'),
      module: this.module,
      filters: {},
    };

    return this.enrichRequestByType(baseRequest, reportType);
  }

  private isValidDataType(dataType: string): dataType is 'excel' | 'csv' {
    return ['excel', 'csv'].includes(dataType);
  }

  private isValidReportType(
    reportType: string
  ): reportType is 'current' | 'range' | 'all' {
    return ['current', 'range', 'all'].includes(reportType);
  }

  private enrichRequestByType(
    request: DownloadReportRequest,
    reportType: string
  ): DownloadReportRequest {
    if (!request.filters) {
      request.filters = {};
    }
    this.addDateFilters(request);
    this.addRelevantFilters(request);

    switch (reportType) {
      case 'current':
        return this.addPaginationAndFilters(request);

      case 'range':
        return request;

      default:
        return request;
    }
  }

  private addDateFilters(request: DownloadReportRequest): void {
    const { startDate, endDate, fromDate, toDate } = this.currentFilters || {};

    const startDateValue = startDate || fromDate;
    const endDateValue = endDate || toDate;

    if (startDateValue) {
      request.startDate = this.formatDateForAPI(startDateValue);
    }

    if (endDateValue) {
      request.endDate = this.formatDateForAPI(endDateValue);
    }
  }

  private addRelevantFilters(request: DownloadReportRequest): void {
    if (this.currentFilters) {
      const relevantFilters = this.extractRelevantFilters(this.currentFilters);
      if (!request.filters) {
        request.filters = {};
      }
      if (Object.keys(relevantFilters).length > 0) {
        request.filters = {
          ...request.filters,
          ...relevantFilters,
        };
      }
    }
  }

  private addPaginationAndFilters(
    request: DownloadReportRequest
  ): DownloadReportRequest {
    const { sortProp, sortDir, ...otherFilters } = this.currentFilters || {};

    return {
      ...request,
      limit: this.currentLimit,
      page: this.currentPage,
      sortProp: sortProp,
      sortDir: typeof sortDir === 'string' ? parseInt(sortDir) : sortDir,
    };
  }

  private formatDateForAPI(date: any): string {
    if (!date) return '';

    return typeof date === 'string' && date.includes('T')
      ? date
      : new Date(date).toISOString();
  }

  private extractRelevantFilters(filters: any): any {
    const { startDate, endDate, fromDate, toDate, ...relevantFilters } =
      filters;

    return Object.keys(relevantFilters).reduce((cleanFilters, key) => {
      const value = relevantFilters[key];
      if (this.isValidFilterValue(value)) {
        cleanFilters[key] = value;
      }
      return cleanFilters;
    }, {} as any);
  }

  private isValidFilterValue(value: any): boolean {
    return value !== undefined && value !== null && value !== '';
  }
}
